/**
 * GrcDeliberationsPage — le registre des délibérations (D1, mig 268).
 *
 * Décision n° 5 : la brique vit dans GRC. Deux volets sur une page :
 *
 *   · les SÉANCES — la date pivot, celle autour de laquelle tout s'organise ;
 *   · le REGISTRE — recherche par numéro, par sujet, par texte : l'usage
 *     quotidien décrit au cadrage (« un agent rédige un acte et doit viser la
 *     bonne »).
 *
 * Les sujets viennent du référentiel FERMÉ de D0 : on choisit, on ne crée pas.
 * Le lien vers Paramètres → Délibérations est affiché quand la liste est vide,
 * pour que le manager sache où ça se règle — et par qui.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Alert, Button, Badge, ModuleHeader } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import {
  Landmark, Plus, Loader2, AlertCircle, Info, CalendarDays, Search,
  Paperclip, Upload, Trash2, FileCheck2, CircleDashed, GitBranch, Clock, Stamp, Undo2,
} from 'lucide-react';
import { ParcoursDeliberation } from '@/components/deliberations/ParcoursDeliberation';

interface Seance {
  id: string; dateSeance: string; heure: string | null;
  lieuNom: string | null; typeSeance: string;
  statut: 'prevue' | 'tenue' | 'annulee'; nbDeliberations: number;
}
interface Sujet { id: string; libelle: string }
interface LignePiece {
  attendueId: string | null; code: string | null; libelle: string;
  obligatoire: boolean;
  /** D2-03 — exigée seulement à partir de l'étape où elle le devient. */
  exigee: boolean;
  deposees: Array<{ id: string; nomFichier: string; mimeType: string; tailleOctets: number }>;
}
interface Parcours {
  id: string;
  definitionLibelle: string;
  etat: { id: string; code: string; libelle: string; couleur: string | null };
  transitionsDisponibles: Array<{
    id: string; libelle: string; roleMinimum: string;
    pieceRequise: boolean; autorisee: boolean;
  }>;
}
interface EtapeCalendrier {
  code: string; libelle: string; ordre: number;
  dateAttendue: string | null; depassee: boolean;
}
interface Delib {
  /** `null` tant que le projet n'est pas programmé à une séance (mig 272). */
  id: string; numero: string | null; objet: string; dateSeance: string | null;
  /** Les vraies dates du parcours (mig 273) — saisies, jamais déduites. */
  redactionDebutLe: string;
  redactionTermineeLe: string | null;
  inscriteOdjLe: string | null;
  resultat: string;
  pour: number | null; contre: number | null; abstentions: number | null;
  sujets: Sujet[];
}

const RESULTATS: Record<string, { label: string; variant: 'neutral' | 'success' | 'urgent' | 'info' }> = {
  en_preparation: { label: 'En préparation', variant: 'neutral' },
  inscrite: { label: 'À l\u2019ordre du jour', variant: 'info' },
  adoptee: { label: 'Adoptée', variant: 'success' },
  rejetee: { label: 'Rejetée', variant: 'neutral' },
  retiree: { label: 'Retirée', variant: 'neutral' },
  ajournee: { label: 'Ajournée', variant: 'urgent' },
};

function dateFr(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', day: '2-digit', month: 'long', year: 'numeric',
  }).format(new Date(`${iso}T12:00:00Z`));
}

/**
 * Nombre de jours entre deux dates de CALENDRIER.
 *
 * Construit sur les composants locaux, jamais sur un horodatage : deux dates à
 * minuit local ne peuvent pas produire de décalage de fuseau. Charte du projet.
 */
/** La date du jour en AAAA-MM-JJ, heure locale. Jamais `toISOString()`. */
function aujourdHui(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function joursEntre(debut: string, fin: string): number {
  const d = debut.split('-').map(Number);
  const f = fin.split('-').map(Number);
  const a = new Date(d[0]!, (d[1] ?? 1) - 1, d[2] ?? 1);
  const b = new Date(f[0]!, (f[1] ?? 1) - 1, f[2] ?? 1);
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86_400_000));
}

const champ =
  'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink';
const label =
  'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';

export default function GrcDeliberationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // `useConfirmDialog`, JAMAIS `window.confirm` : incompatible Capacitor, et
  // la boîte native casse la charte glassmorphism.
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  const peutGerer = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');
  const estAdmin = ['super_admin', 'admin'].includes(user?.role ?? '');

  const [seances, setSeances] = useState<Seance[]>([]);
  const [sujets, setSujets] = useState<Sujet[]>([]);
  const [delibs, setDelibs] = useState<Delib[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * L'ID de la délibération concernée par le message, quand il y en a une.
   *
   * Retour d'Henrico le 29/07 : « le message se met tout en haut et on ne voit
   * pas qu'il y a une erreur ». Avec 3 délibérations à l'écran, l'action est à
   * 800 px du message. On l'affiche donc AU PLUS PRÈS de ce qui a été cliqué.
   */
  const [errorCible, setErrorCible] = useState<string | null>(null);

  /** Pose un message rattaché à une ligne précise. */
  function erreurSur(cible: string | null, message: string) {
    setErrorCible(cible); setError(message);
  }

  // Filtres du registre — la recherche est l'usage quotidien.
  const [q, setQ] = useState('');
  const [filtreSujet, setFiltreSujet] = useState('');

  // Nouvelle séance
  const [formSeance, setFormSeance] = useState(false);
  const [sDate, setSDate] = useState('');
  const [sHeure, setSHeure] = useState('');

  // Les pièces de la délibération ouverte (D2). Chargées à la demande : la
  // liste du registre n'a pas besoin de les connaître.
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [pieces, setPieces] = useState<LignePiece[]>([]);
  const [piecesLoading, setPiecesLoading] = useState(false);
  const [parcours, setParcours] = useState<Parcours | null>(null);
  const [calendrier, setCalendrier] = useState<EtapeCalendrier[]>([]);
  const [historique, setHistorique] = useState<Array<{
    le: string | null; de: string | null; vers: string;
    par: string | null; commentaire: string | null;
  }>>([]);
  const [suites, setSuites] = useState<{
    disponible: boolean; motif: string | null;
    suites: Array<{ modeleId: string; libelle: string; lien: string }>;
  } | null>(null);

  // Nouvelle délibération
  const [formDelib, setFormDelib] = useState(false);
  const [dSeance, setDSeance] = useState('');
  const [dObjet, setDObjet] = useState('');
  const [dSujets, setDSujets] = useState<string[]>([]);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rs, rr, rf] = await Promise.all([
        api.deliberations.seances(),
        api.deliberations.registre({
          ...(q.trim() ? { q: q.trim() } : {}),
          ...(filtreSujet ? { sujetId: filtreSujet } : {}),
        }),
        api.deliberations.referentiels(),
      ]);
      setSeances(rs.seances);
      setDelibs(rr.deliberations);
      setSujets(rf.sujets.map((s) => ({ id: s.id, libelle: s.libelle })));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, [q, filtreSujet]);
  useEffect(() => { void charger(); }, [charger]);

  async function creerSeance() {
    if (!sDate) { setError('La date de la séance est attendue'); return; }
    setSaving(true); setError(null);
    try {
      await api.deliberations.creerSeance({ dateSeance: sDate, heure: sHeure || null });
      setSDate(''); setSHeure(''); setFormSeance(false);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Création impossible');
    } finally { setSaving(false); }
  }

  async function creerDelib() {
    // La séance n'est plus obligatoire (mig 272) : on rédige un projet AVANT de
    // savoir à quelle séance il ira. Sans séance, il n'a pas de numéro — il en
    // recevra un au moment où on le programmera.
    if (dObjet.trim().length < 3) {
      setError('Donnez un objet à la délibération'); return;
    }
    setSaving(true); setError(null);
    try {
      await api.deliberations.creerDeliberation({
        seanceId: dSeance || null, objet: dObjet.trim(),
        ...(dSujets.length > 0 ? { sujetIds: dSujets } : {}),
      });
      setDObjet(''); setDSujets([]); setFormDelib(false);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Création impossible');
    } finally { setSaving(false); }
  }

  /**
   * Programme un projet à une séance. C'est ce geste qui l'inscrit au registre
   * et lui attribue son numéro — d'où la confirmation : on ne défait pas une
   * numérotation.
   */
  async function programmer(d: Delib, seanceId: string) {
    if (!seanceId) return;
    const s = seances.find((x) => x.id === seanceId);
    const ok = await confirm({
      title: 'Inscrire au registre ?',
      description: `« ${d.objet} » sera rattachée à la séance${s ? ` du ${dateFr(s.dateSeance)}` : ''} et recevra son numéro de délibération. Le numéro est définitif.`,
      confirmLabel: 'Inscrire',
    });
    if (!ok) return;
    setError(null);
    try {
      await api.deliberations.programmerDeliberation(d.id, seanceId);
      await charger();
    } catch (e) {
      erreurSur(d.id, e instanceof ApiException ? e.message : 'Programmation impossible');
    }
  }

  /**
   * Enregistre une des deux dates saisies du parcours (mig 273).
   *
   * Champ date explicite, avec son calendrier — PAS un bouton qui estampille le
   * jour du clic. L'ordre du jour est souvent arrêté en réunion, plusieurs jours
   * avant qu'on le saisisse : la date du clic aurait été fausse la plupart du
   * temps. Demande explicite d'Henrico.
   */
  async function enregistrerDate(
    d: Delib,
    champ: 'redactionTermineeLe' | 'inscriteOdjLe',
    valeur: string,
  ) {
    setError(null);
    try {
      await api.deliberations.majDeliberation(d.id, { [champ]: valeur || null });
      await charger();
    } catch (e) {
      erreurSur(d.id, e instanceof ApiException ? e.message : 'Date non enregistrée');
    }
  }

  /**
   * Supprime une délibération créée par erreur.
   *
   * Le serveur seul sait ce qui bloque (pièce déposée, arrêté engendré, vote,
   * numéro au registre) : on affiche son message tel quel, il énumère les
   * raisons plutôt que de dire « non ».
   */
  async function supprimerDelib(d: Delib) {
    const ok = await confirm({
      title: 'Supprimer cette délibération ?',
      description: `« ${d.objet} » sera effacée. Ce n’est possible que si rien n’a encore été fait dessus — aucune pièce, aucun arrêté, aucun vote, aucun numéro.`,
      danger: true, confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    setError(null); setErrorCible(null);
    try {
      await api.deliberations.supprimerDeliberation(d.id);
      await charger();
    } catch (e) {
      erreurSur(d.id, e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  /** Supprime une séance créée par erreur — refusée si des délibs y sont inscrites. */
  async function supprimerSeanceDuConseil(se: Seance) {
    const ok = await confirm({
      title: 'Supprimer cette séance ?',
      description: `La séance du ${dateFr(se.dateSeance)} sera effacée. Ce n’est possible qu’à condition qu’aucune délibération n’y soit inscrite.`,
      danger: true, confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    setError(null); setErrorCible(null);
    try {
      await api.deliberations.supprimerSeance(se.id);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  /**
   * Revient à l'étape précédente du parcours.
   *
   * Le motif est obligatoire et reste dans l'historique : ce n'est pas un
   * effacement mais un événement de plus. Le registre montre l'erreur ET sa
   * correction — plus honnête qu'une trace qui disparaît.
   */
  async function retourEtape(d: Delib) {
    const motif = await prompt({
      title: 'Revenir à l’étape précédente',
      description: 'Le motif restera dans l’historique de la délibération. Rien n’est effacé : le retour est enregistré comme une étape de plus.',
      placeholder: 'Ex. : clic par erreur, l’ordre du jour n’était pas arrêté',
      required: true, maxLength: 300,
      confirmLabel: 'Revenir en arrière',
    });
    if (!motif) return;
    setError(null); setErrorCible(null);
    try {
      await api.deliberations.retourEtapePrecedente(d.id, motif);
      await ouvrirPiecesForce(d.id);
      await charger();
    } catch (e) {
      erreurSur(d.id, e instanceof ApiException ? e.message : 'Retour impossible');
    }
  }

  /** Recharge le panneau d'une délibération sans le refermer. */
  async function ouvrirPiecesForce(id: string) {
    if (ouverte === id) { setOuverte(null); await ouvrirPieces(id); }
  }

  async function ouvrirPieces(id: string) {
    if (ouverte === id) { setOuverte(null); return; }
    setOuverte(id); setPiecesLoading(true); setError(null);
    try {
      // Pièces, parcours, suites ET historique d'un coup : c'est le même
      // panneau — désormais appelé « Dossier », parce qu'il contient tout ça.
      const [rp, rw, rs, rh] = await Promise.all([
        api.deliberations.pieces(id),
        api.deliberations.parcours(id),
        api.deliberations.suites(id),
        api.deliberations.historiqueParcours(id),
      ]);
      setPieces(rp.pieces);
      setParcours(rw.parcours);
      setCalendrier(rw.calendrier);
      setSuites(rs);
      setHistorique(rh.historique);
    } catch (e) {
      erreurSur(id, e instanceof ApiException ? e.message : 'Dossier illisible');
    } finally { setPiecesLoading(false); }
  }

  /**
   * ─── DÉPOSER UNE PIÈCE — ET CE QU'ELLE PROPOSE ────────────────────────────
   *
   * Arbitrage du 29/07 avec Henrico. Son idée de départ : que le dépôt d'une
   * pièce FASSE avancer le parcours tout seul, pour ne plus dépendre d'un bouton
   * qu'on clique par erreur. Le fond est juste — ce qui fait avancer une
   * procédure, c'est qu'on ait produit le document.
   *
   * Mais l'automatisme complet a été écarté, pour trois raisons :
   *   · AUCUNE des cinq pièces ne prouve l'inscription à l'ordre du jour — ce
   *     serait la convocation, elle n'existe pas dans la liste ;
   *   · le procès-verbal se rédige souvent DES JOURS après la séance : le
   *     laisser piloter « votée » afficherait une date fausse ;
   *   · retirer une pièce ne doit pas faire reculer — le cas courant est de
   *     remplacer un PDF mal scanné, une correction bénigne ne doit pas défaire
   *     le parcours.
   *
   * D'où : le dépôt PROPOSE, l'utilisateur décide. Le geste utile est conservé
   * (c'est le dépôt qui déclenche, pas un bouton isolé), la décision reste
   * humaine, et un clic de trop ne fait plus rien tout seul.
   */
  async function deposer(delibId: string, attendueId: string | null, fichier: File) {
    setError(null); setErrorCible(null);
    try {
      await api.deliberations.deposerPiece(delibId, fichier, attendueId);
      const r = await api.deliberations.pieces(delibId);
      setPieces(r.pieces);

      // Le projet de délibération déposé = le texte est bouclé. On le PROPOSE.
      const ligne = r.pieces.find((x) => x.attendueId === attendueId);
      const delib = delibs.find((x) => x.id === delibId);
      if (ligne?.code === 'projet' && delib && !delib.redactionTermineeLe) {
        const jour = await prompt({
          title: 'Rédaction terminée ?',
          description: 'Vous venez de déposer le projet de délibération. Si le texte est bouclé, confirmez la date — elle restera modifiable.',
          defaultValue: aujourdHui(),
          confirmLabel: 'Enregistrer la date',
          cancelLabel: 'Pas encore',
        });
        if (jour && /^\d{4}-\d{2}-\d{2}$/.test(jour)) {
          await api.deliberations.majDeliberation(delibId, { redactionTermineeLe: jour });
        }
      }
      await charger();
    } catch (e) {
      erreurSur(delibId, e instanceof ApiException ? e.message : 'Dépôt impossible');
    }
  }

  async function franchir(delibId: string, transitionId: string, piece?: File) {
    setError(null);
    try {
      await api.deliberations.avancerParcours(delibId, transitionId, piece);
      const [rw, rr] = await Promise.all([
        api.deliberations.parcours(delibId),
        api.deliberations.registre({
          ...(q.trim() ? { q: q.trim() } : {}),
          ...(filtreSujet ? { sujetId: filtreSujet } : {}),
        }),
      ]);
      setParcours(rw.parcours); setCalendrier(rw.calendrier);
      setDelibs(rr.deliberations);   // le résultat a suivi l'étape franchie
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Étape impossible');
    }
  }

  async function rattraperParcours(delibId: string) {
    setError(null);
    try {
      await api.deliberations.ouvrirParcours(delibId);
      const rw = await api.deliberations.parcours(delibId);
      setParcours(rw.parcours); setCalendrier(rw.calendrier);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Ouverture impossible');
    }
  }

  async function ouvrirFichier(pieceId: string) {
    try {
      const { url } = await api.deliberations.urlPiece(pieceId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Fichier indisponible');
    }
  }

  async function retirer(delibId: string, pieceId: string, nom: string) {
    // Un PV retiré par erreur part en corbeille R2 et se récupère — mais
    // l'agent ne le sait pas. On demande.
    const ok = await confirm({
      title: 'Retirer cette pièce ?',
      description: `« ${nom} » sera retirée de la délibération.`,
      confirmLabel: 'Retirer',
      danger: true,
    });
    if (!ok) return;
    setError(null);
    try {
      await api.deliberations.retirerPiece(pieceId);
      const r = await api.deliberations.pieces(delibId);
      setPieces(r.pieces);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Retrait impossible');
    }
  }

  return (
    <GrcLayout active="deliberations">
      <ModuleHeader
        accent="grc" icon={Landmark}
        eyebrow="GRC & communication" title="Délibérations du conseil"
      />

      <main id="main-content" className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 space-y-4">
        {/* Seuls les messages SANS cible restent en tête : les autres
            s'affichent contre la ligne concernée (voir plus bas). */}
        {error && !errorCible && (
          <Alert variant="error" icon={AlertCircle}>{error}</Alert>
        )}
        {/* Refonte UX, chantier 1 (D5) — les référentiels du conseil quittent le menu
            d'Administration : on y accède d'ici, là où l'on s'en sert. */}
        <div className="flex justify-end">
          <a href="/admin/deliberations" className="text-sm text-accent underline">Référentiels du conseil (matières, sujets, pièces attendues)</a>
        </div>

        {/* ══ Les séances ══ */}
        <Card variant="glass" padded>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 font-medium text-ink">
              <CalendarDays className="h-4 w-4 text-grc" aria-hidden="true" />
              Séances du conseil
            </h2>
            {peutGerer && (
              <Button variant="glass" size="sm" onClick={() => setFormSeance((v) => !v)}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Nouvelle séance
              </Button>
            )}
          </div>

          {formSeance && (
            <div className="mt-3 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3 sm:grid-cols-3">
              <div>
                <label className={label} htmlFor="se-date">Date</label>
                <input id="se-date" type="date" value={sDate}
                  onChange={(e) => setSDate(e.target.value)} className={champ} />
              </div>
              <div>
                <label className={label} htmlFor="se-heure">Heure</label>
                <input id="se-heure" value={sHeure} placeholder="18h30"
                  onChange={(e) => setSHeure(e.target.value)} className={champ} />
              </div>
              <div className="flex items-end">
                <Button variant="primary" size="sm" loading={saving}
                  onClick={() => void creerSeance()}>
                  Créer la séance
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <p className="mt-3 flex items-center gap-2 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
            </p>
          ) : seances.length === 0 ? (
            <p className="mt-3 py-4 text-center text-sm text-ink-muted">
              Aucune séance. Créez celle du prochain conseil — c'est elle qui
              porte les délibérations.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {seances.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-sm text-ink">
                    {dateFr(s.dateSeance)}
                    {s.heure && <span className="text-ink-muted"> · {s.heure}</span>}
                    {s.lieuNom && <span className="text-ink-muted"> · {s.lieuNom}</span>}
                    {s.typeSeance === 'extraordinaire' && (
                      <span className="ml-2"><Badge variant="urgent" size="sm">Extraordinaire</Badge></span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 text-2xs text-ink-subtle">
                    {s.nbDeliberations} délibération{s.nbDeliberations > 1 ? 's' : ''}
                    {/* Une séance créée par erreur doit pouvoir partir. Le bouton
                        n'apparaît que si elle est vide : inutile de proposer une
                        action qu'on refusera. */}
                    {peutGerer && s.nbDeliberations === 0 && (
                      <button type="button"
                        className="inline-flex items-center gap-1 underline hover:text-urgent-fg"
                        onClick={() => void supprimerSeanceDuConseil(s)}
                        aria-label={`Supprimer la séance du ${dateFr(s.dateSeance)}`}>
                        <Trash2 className="h-3 w-3" aria-hidden="true" /> Supprimer
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* ══ Le registre ══ */}
        <Card variant="glass" padded>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 font-medium text-ink">
              <Search className="h-4 w-4 text-grc" aria-hidden="true" />
              Registre des délibérations
            </h2>
            {peutGerer && (
              <Button variant="primary" size="sm" onClick={() => setFormDelib((v) => !v)}
                disabled={seances.length === 0}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Nouvelle délibération
              </Button>
            )}
          </div>

          {formDelib && (
            <div className="mt-3 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={label} htmlFor="de-seance">
                    Séance <span className="normal-case text-ink-subtle">(facultatif)</span>
                  </label>
                  <select id="de-seance" value={dSeance}
                    onChange={(e) => setDSeance(e.target.value)} className={champ}>
                    <option value="">— Pas encore programmée —</option>
                    {seances.filter((s) => s.statut !== 'annulee').map((s) => (
                      <option key={s.id} value={s.id}>{dateFr(s.dateSeance)}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-ink-subtle">
                    {dSeance
                      ? `Le numéro (DEL-…) suit l’année de la séance.`
                      : 'Sans séance, c’est un projet en rédaction : il n’a pas encore de numéro. '
                        + 'Il en recevra un quand vous le programmerez — un projet abandonné ne '
                        + 'laisse donc aucun trou dans le registre.'}
                  </p>
                </div>
                <div>
                  <label className={label} htmlFor="de-objet">Objet</label>
                  <input id="de-objet" value={dObjet}
                    onChange={(e) => setDObjet(e.target.value)} className={champ}
                    placeholder="Tarifs de la cantine 2027" />
                </div>
              </div>
              <div>
                <span className={label}>Sujets</span>
                {sujets.length === 0 ? (
                  <p className="text-xs text-ink-subtle">
                    Aucun sujet au référentiel.
                    {estAdmin
                      ? <> Créez-les dans <button type="button" className="underline"
                          onClick={() => navigate('/admin/deliberations')}>
                          Paramètres → Délibérations</button>.</>
                      : ' Ils se créent dans les paramètres, par un administrateur.'}
                  </p>
                ) : (
                  /* Cases à cocher, pas de saisie : le référentiel est FERMÉ.
                     Une délibération peut porter PLUSIEURS sujets — c'est la
                     table de liaison du cadrage. */
                  <div className="flex flex-wrap gap-2">
                    {sujets.map((su) => {
                      const coche = dSujets.includes(su.id);
                      return (
                        <button key={su.id} type="button"
                          onClick={() => setDSujets((prev) =>
                            coche ? prev.filter((x) => x !== su.id) : [...prev, su.id])}
                          className={`rounded-r-pill border px-3 py-1 text-xs transition-colors ${
                            coche
                              ? 'border-grc/40 bg-grc-tint/80 text-grc-fg'
                              : 'border-border bg-surface/40 text-ink-muted hover:text-ink'
                          }`}>
                          {su.libelle}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <Button variant="primary" size="sm" loading={saving}
                  onClick={() => void creerDelib()}>
                  Inscrire au registre
                </Button>
              </div>
            </div>
          )}

          {/* Filtres — la recherche est l'usage quotidien. */}
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className={label} htmlFor="f-q">Rechercher</label>
              <input id="f-q" value={q} onChange={(e) => setQ(e.target.value)}
                className={champ} placeholder="DEL-2026-015, cantine, EPCI…" />
            </div>
            <div>
              <label className={label} htmlFor="f-sujet">Par sujet</label>
              <select id="f-sujet" value={filtreSujet}
                onChange={(e) => setFiltreSujet(e.target.value)} className={champ}>
                <option value="">Tous les sujets</option>
                {sujets.map((su) => (
                  <option key={su.id} value={su.id}>{su.libelle}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? null : delibs.length === 0 ? (
            <p className="mt-4 py-6 text-center text-sm text-ink-muted">
              {q || filtreSujet
                ? 'Rien ne correspond à cette recherche.'
                : 'Le registre est vide. La première délibération inscrite recevra le numéro DEL-' +
                  new Date().getFullYear() + '-001.'}
            </p>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {delibs.map((d) => {
                const r = RESULTATS[d.resultat] ?? RESULTATS.en_preparation!;
                return (
                  <li key={d.id} className="py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="min-w-0">
                        {/* Un projet non encore programmé n'a PAS de numéro
                            (mig 272) : le registre reste chronologique et
                            complet, un projet abandonné ne laisse pas de trou. */}
                        {d.numero
                          ? <span className="font-medium tabular-nums text-ink">{d.numero}</span>
                          : <Badge variant="neutral" size="sm">Projet — non programmé</Badge>}
                        <span className="ml-2 text-sm text-ink">{d.objet}</span>
                      </span>
                      <Badge variant={r.variant} size="sm">{r.label}</Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-2xs text-ink-subtle">
                      {d.dateSeance ? (
                        <span>séance du {dateFr(d.dateSeance)}</span>
                      ) : peutGerer ? (
                        // Programmer, c'est inscrire au registre : c'est ici et
                        // nulle part ailleurs que le numéro est attribué.
                        <label className="inline-flex items-center gap-1">
                          <span className="text-ink-subtle">Programmer à :</span>
                          <select
                            aria-label={`Programmer « ${d.objet} » à une séance`}
                            defaultValue=""
                            onChange={(e) => { void programmer(d, e.target.value); e.target.value = ''; }}
                            className="rounded-r-md border border-border bg-surface/60 px-1.5 py-0.5 text-2xs text-ink"
                          >
                            <option value="">— choisir une séance —</option>
                            {seances.map((se) => (
                              <option key={se.id} value={se.id}>{dateFr(se.dateSeance)}</option>
                            ))}
                          </select>
                        </label>
                      ) : (
                        <span>pas encore rattaché à une séance</span>
                      )}
                      {d.pour !== null && (
                        <span>· {d.pour} pour, {d.contre ?? 0} contre, {d.abstentions ?? 0} abst.</span>
                      )}
                      {d.sujets.map((su) => (
                        <Badge key={su.id} variant="neutral" size="sm">{su.libelle}</Badge>
                      ))}
                      <span className="ml-auto inline-flex items-center gap-3">
                        {/* D4 — la délibération devient SOURCE d'un acte. Le
                            numéro et la date partent seuls dans les visas :
                            68 occurrences du catalogue résolues d'un coup. */}
                        {peutGerer && (
                          <button type="button" className="inline-flex items-center gap-1 underline"
                            onClick={() => navigate(
                              `/actes/nouveau?sourceType=deliberation&sourceId=${d.id}`)}>
                            <Stamp className="h-3 w-3" aria-hidden="true" />
                            Générer un arrêté
                          </button>
                        )}
                        {/* Supprimer : proposé seulement tant que rien n'a été
                            fait — pas de numéro, pas de vote. Le serveur
                            revérifie (pièces, arrêtés) et dit ce qui bloque. */}
                        {peutGerer && !d.numero && d.resultat === 'en_preparation' && (
                          <button type="button"
                            className="inline-flex items-center gap-1 underline hover:text-urgent-fg"
                            onClick={() => void supprimerDelib(d)}>
                            <Trash2 className="h-3 w-3" aria-hidden="true" /> Supprimer
                          </button>
                        )}
                        <button type="button" className="inline-flex items-center gap-1 underline"
                          onClick={() => void ouvrirPieces(d.id)}>
                          <Paperclip className="h-3 w-3" aria-hidden="true" />
                          {/* « Dossier » et pas « Pièces » : le panneau contient
                              le parcours, les dates ET les pièces. Qui ne cherche
                              pas à déposer un fichier n'aurait jamais cliqué —
                              retour d'Henrico du 29/07. Court, donc mobile OK. */}
                          {ouverte === d.id ? 'Fermer le dossier' : 'Dossier'}
                        </button>
                      </span>
                    </div>

                    {/* Le message de CETTE ligne, juste sous ce qu'on a cliqué. */}
                    {error && errorCible === d.id && (
                      <div className="mt-2">
                        <Alert variant="error" icon={AlertCircle}>{error}</Alert>
                      </div>
                    )}

                    {/* ── D2 : ce qui est attendu, et ce qui MANQUE ────────── */}
                    {ouverte === d.id && (
                      <div className="mt-3 rounded-r-md border border-border bg-surface/40 p-3">
                        {piecesLoading ? (
                          <p className="flex items-center gap-2 text-sm text-ink-muted">
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
                          </p>
                        ) : (
                          <>
                            {/* ── D3-01 : où en est la délibération ────────── */}
                            <div className="mb-3 border-b border-border pb-3">
                              {!parcours ? (
                                /* D3-03 — le parcours s'ouvre en best-effort à la
                                   création. S'il a échoué, on le dit ET on le
                                   répare, au lieu de laisser un silence. */
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="text-2xs text-ink-subtle">
                                    Aucun parcours ouvert sur cette délibération.
                                  </span>
                                  {peutGerer && (
                                    <Button variant="glass" size="sm"
                                      onClick={() => void rattraperParcours(d.id)}>
                                      <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
                                      Ouvrir le parcours
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <>
                                  {/* Refonte UX, chantier 5 (maquette 9) — le parcours pas à pas. */}
                                  <ParcoursDeliberation etatCode={parcours.etat.code} calendrier={calendrier}
                                    pieces={pieces} dateSeance={d.dateSeance ?? null} />
                                  <div className="flex flex-wrap items-center gap-2">
                                    <GitBranch className="h-3.5 w-3.5 text-grc" aria-hidden="true" />
                                    <span className="text-sm text-ink">{parcours.etat.libelle}</span>
                                    {parcours.transitionsDisponibles
                                      .filter((t) => t.autorisee)
                                      .map((t) => (
                                        t.pieceRequise ? (
                                          <label key={t.id}
                                            className="inline-flex cursor-pointer items-center gap-1 rounded-r-pill border border-border px-2 py-1 text-2xs text-ink-muted hover:text-ink">
                                            <Upload className="h-3 w-3" aria-hidden="true" />
                                            {t.libelle}
                                            <input type="file" className="hidden" accept=".pdf,.jpg,.png"
                                              onChange={(e) => {
                                                const f = e.target.files?.[0];
                                                if (f) void franchir(d.id, t.id, f);
                                                e.target.value = '';
                                              }} />
                                          </label>
                                        ) : (
                                          <Button key={t.id} variant="glass" size="sm"
                                            onClick={() => void franchir(d.id, t.id)}>
                                            {t.libelle}
                                          </Button>
                                        )
                                      ))}
                                    {/* Marche arrière — demande d'Henrico le
                                        29/07 : « j'ai cliqué pour voir, je me
                                        suis trompé ». Discret, à droite, mais
                                        toujours là : un clic malheureux ne doit
                                        pas poser un statut faux pour toujours.
                                        Ce n'est PAS un effacement — le retour
                                        s'ajoute à l'historique avec son motif. */}
                                    {peutGerer && (
                                      <button type="button"
                                        className="ml-auto inline-flex items-center gap-1 text-2xs text-ink-subtle underline hover:text-ink"
                                        onClick={() => void retourEtape(d)}>
                                        <Undo2 className="h-3 w-3" aria-hidden="true" />
                                        Revenir à l’étape précédente
                                      </button>
                                    )}
                                  </div>

                                  {/* ── LE PARCOURS RÉEL (mig 273) ─────────────
                                      Les étapes FAITES affichent la date saisie ;
                                      celles à venir gardent le repère calculé
                                      depuis la séance.

                                      Deux champs date explicites, avec leur
                                      calendrier — pas un bouton qui estampille le
                                      jour du clic : l'ordre du jour est arrêté en
                                      réunion, souvent des jours avant la saisie. */}
                                  <div className="mt-3 space-y-2 rounded-r-md border border-border bg-surface/40 p-2.5">

                                    {/* Rédaction : une PÉRIODE, du projet à sa fin */}
                                    <div className="flex flex-wrap items-center gap-2 text-2xs">
                                      <span className="w-44 shrink-0 text-ink">Projet en rédaction</span>
                                      <span className="text-ink-subtle">
                                        depuis le {dateFr(d.redactionDebutLe)}
                                      </span>
                                      <label className="inline-flex items-center gap-1">
                                        <span className="text-ink-subtle">terminée le</span>
                                        <input
                                          type="date"
                                          aria-label="Date de fin de rédaction du projet"
                                          disabled={!peutGerer}
                                          value={d.redactionTermineeLe ?? ''}
                                          onChange={(e) => void enregistrerDate(d, 'redactionTermineeLe', e.target.value)}
                                          className="rounded-r-md border border-border bg-surface/60 px-1.5 py-0.5 text-2xs text-ink"
                                        />
                                      </label>
                                      {d.redactionTermineeLe && (
                                        <span className="text-ink-subtle">
                                          ({joursEntre(d.redactionDebutLe, d.redactionTermineeLe)} j)
                                        </span>
                                      )}
                                    </div>

                                    {/* Ordre du jour : un JOUR, celui où le conseil l'a arrêté */}
                                    <div className="flex flex-wrap items-center gap-2 text-2xs">
                                      <span className="w-44 shrink-0 text-ink">Inscrite à l’ordre du jour</span>
                                      <label className="inline-flex items-center gap-1">
                                        <span className="text-ink-subtle">le</span>
                                        <input
                                          type="date"
                                          aria-label="Date d’inscription à l’ordre du jour"
                                          disabled={!peutGerer || !d.dateSeance}
                                          value={d.inscriteOdjLe ?? ''}
                                          onChange={(e) => void enregistrerDate(d, 'inscriteOdjLe', e.target.value)}
                                          className="rounded-r-md border border-border bg-surface/60 px-1.5 py-0.5 text-2xs text-ink disabled:opacity-50"
                                        />
                                      </label>
                                      {!d.dateSeance && (
                                        <span className="text-ink-subtle">
                                          — programmez d’abord à une séance : un ordre du jour est celui d’un conseil
                                        </span>
                                      )}
                                    </div>

                                    {/* Séance : la date du conseil, déjà connue */}
                                    <div className="flex flex-wrap items-center gap-2 text-2xs">
                                      <span className="w-44 shrink-0 text-ink">Votée en séance</span>
                                      <span className="text-ink-subtle">
                                        {d.dateSeance ? `le ${dateFr(d.dateSeance)}` : 'séance non fixée'}
                                      </span>
                                    </div>

                                    {/* ── L'HISTORIQUE RÉEL ────────────────────
                                        La trace existe dans `workflow_evenements`
                                        depuis la mig 244 et n'était affichée
                                        NULLE PART. C'est elle qui répond à
                                        « quand ça a été fait, et par qui » — y
                                        compris les retours en arrière, qui
                                        apparaissent comme des étapes de plus. */}
                                    {historique.length > 0 && (
                                      <details className="pt-1">
                                        <summary className="cursor-pointer text-2xs text-ink-subtle">
                                          Historique ({historique.length})
                                        </summary>
                                        <ul className="mt-1 space-y-0.5">
                                          {historique.map((h, i) => (
                                            <li key={i} className="text-2xs text-ink-subtle">
                                              {dateFr(h.le)} — {h.vers}
                                              {h.par ? ` · ${h.par}` : ''}
                                              {h.commentaire ? ` · ${h.commentaire}` : ''}
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}

                                    {/* Ce qui RESTE à venir garde le repère calculé. */}
                                    {calendrier.some((c) => c.dateAttendue && !c.depassee) && (
                                      <p className="pt-1 text-2xs text-ink-subtle">
                                        <Clock className="mr-1 inline h-3 w-3" aria-hidden="true" />
                                        À venir, calculé depuis la séance :{' '}
                                        {calendrier
                                          .filter((c) => c.dateAttendue && !c.depassee)
                                          .map((c) => `${c.libelle} pour le ${dateFr(c.dateAttendue)}`)
                                          .join(' · ')}
                                      </p>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>

                            {/* ── D5 : ce qui en découle ──────────────────── */}
                            {suites?.disponible && suites.suites.length > 0 && (
                              <div className="mb-3 rounded-r-md border border-grc/30 bg-grc-tint/30 p-2.5">
                                <p className="text-2xs font-medium text-ink">
                                  Cette délibération peut enclencher :
                                </p>
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  {suites.suites.map((su) => (
                                    <button key={su.modeleId} type="button"
                                      onClick={() => navigate(su.lien)}
                                      className="rounded-r-pill border border-border bg-surface/60 px-2.5 py-1 text-2xs text-ink-muted hover:text-ink">
                                      {su.libelle}
                                    </button>
                                  ))}
                                </div>
                                {/* Le principe, écrit à l'écran : on engendre un
                                    brouillon, jamais un acte signé. */}
                                <p className="mt-1.5 text-2xs text-ink-subtle">
                                  Un brouillon sera ouvert, avec le numéro et la
                                  date de cette délibération déjà repris. Rien
                                  n'est signé sans vous.
                                </p>
                              </div>
                            )}

                          <ul className="space-y-2">
                            {pieces.map((p) => (
                              <li key={p.attendueId ?? 'libres'}
                                  className="flex flex-wrap items-start justify-between gap-2">
                                <span className="min-w-0">
                                  <span className="inline-flex items-center gap-1.5 text-sm text-ink">
                                    {p.deposees.length > 0
                                      ? <FileCheck2 className="h-3.5 w-3.5 text-grc" aria-hidden="true" />
                                      : <CircleDashed className="h-3.5 w-3.5 text-ink-subtle" aria-hidden="true" />}
                                    {p.libelle}
                                  </span>
                                  {p.deposees.length === 0 ? (
                                    /* D2-03 — l'obligation est datée par l'étape.
                                       Avant, la case est vide sans reproche ;
                                       après, elle manque vraiment. */
                                    <span className={`ml-2 text-2xs ${p.exigee ? 'text-urgent-fg' : 'text-ink-subtle'}`}>
                                      {p.exigee ? '— manquante à ce stade' : '— non fournie'}
                                    </span>
                                  ) : (
                                    <ul className="ml-5 mt-1 space-y-0.5">
                                      {p.deposees.map((f) => (
                                        <li key={f.id} className="flex items-center gap-2 text-2xs">
                                          <button type="button" className="underline text-ink-muted"
                                            onClick={() => void ouvrirFichier(f.id)}>
                                            {f.nomFichier}
                                          </button>
                                          <span className="text-ink-subtle">
                                            {Math.max(1, Math.round(f.tailleOctets / 1024))} Ko
                                          </span>
                                          {peutGerer && (
                                            <button type="button" aria-label={`Retirer ${f.nomFichier}`}
                                              onClick={() => void retirer(d.id, f.id, f.nomFichier)}
                                              className="text-ink-subtle hover:text-ink">
                                              <Trash2 className="h-3 w-3" aria-hidden="true" />
                                            </button>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </span>
                                {peutGerer && p.attendueId !== null && (
                                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-r-pill border border-border px-2 py-1 text-2xs text-ink-muted hover:text-ink">
                                    <Upload className="h-3 w-3" aria-hidden="true" /> Déposer
                                    <input type="file" className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.odt"
                                      onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) void deposer(d.id, p.attendueId, f);
                                        e.target.value = '';
                                      }} />
                                  </label>
                                )}
                              </li>
                            ))}
                          </ul>
                          </>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <p className="mt-3 flex items-start gap-2 text-xs text-ink-subtle">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            Les votes sont enregistrés en décompte (pour, contre, abstentions) —
            jamais nominativement. Une délibération rejetée garde son numéro :
            le registre est chronologique et complet.
          </p>
        </Card>
      </main>
      {dialogElement}
    </GrcLayout>
  );
}
